import { useState } from "react";
import { SparkleIcon } from "./Icons";
import { setNameOnce } from "./nameSetup";
import * as sounds from "./sounds";

/**
 * The one-time "what should we call you?" step — shown to anyone (new or
 * existing) who hasn't locked in a name yet. Deliberately has no close
 * button and doesn't dismiss on a backdrop click; this only ever appears
 * once, so there's no "skip for now" to design around.
 */
export default function NameSetupModal({
  initialFirst,
  initialLast,
  onDone,
}: {
  initialFirst: string;
  initialLast: string;
  onDone: () => void;
}) {
  const [step, setStep] = useState<"edit" | "confirm">("edit");
  const [first, setFirst] = useState(initialFirst);
  const [last, setLast] = useState(initialLast);
  const [saving, setSaving] = useState(false);

  const canContinue = first.trim().length > 0 && last.trim().length > 0;

  const confirm = async () => {
    setSaving(true);
    try {
      await setNameOnce(first.trim(), last.trim());
      sounds.playOnce(sounds.SOUND.click);
      onDone();
    } catch (e) {
      console.warn("name setup failed", e);
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card namesetup-card">
        <div className="release-hero namesetup-hero">
          <span className="release-hero__icon">
            <SparkleIcon size={20} />
          </span>
          <div>
            <div className="release-hero__title">
              {step === "edit" ? "What should we call you?" : "Just to confirm"}
            </div>
            <div className="namesetup-hero__sub">
              {step === "edit"
                ? "This is how you'll appear to everyone in chat — set once, so make sure it's right."
                : "This can't be changed again once you confirm."}
            </div>
          </div>
        </div>

        {step === "edit" ? (
          <div className="modal-section">
            <div className="modal-label">First name</div>
            <div className="modal-row namesetup-row">
              <input
                autoFocus
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                placeholder="First name"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canContinue) setStep("confirm");
                }}
              />
            </div>
            <div className="modal-label">Last name</div>
            <div className="modal-row namesetup-row">
              <input
                value={last}
                onChange={(e) => setLast(e.target.value)}
                placeholder="Last name"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canContinue) setStep("confirm");
                }}
              />
            </div>
          </div>
        ) : (
          <div className="modal-section">
            <div className="namesetup-preview">
              {first.trim()} {last.trim()}
            </div>
          </div>
        )}

        <div className="namesetup-foot">
          {step === "edit" ? (
            <button className="modal-btn" disabled={!canContinue} onClick={() => setStep("confirm")}>
              Continue
            </button>
          ) : (
            <>
              <button className="namesetup-back" disabled={saving} onClick={() => setStep("edit")}>
                Go back
              </button>
              <button className="modal-btn" disabled={saving} onClick={() => void confirm()}>
                {saving ? "Saving…" : "Confirm, this is right"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
